import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { DateWheelPicker } from "@/components/ui/DateWheelPicker";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { formatDateForDatabase } from "@/utils/dateHelpers";

interface TimelineSetupCTAProps {
  userId: string;
  onTimelineSetup?: () => void;
}

export const TimelineSetupCTA = ({ userId, onTimelineSetup }: TimelineSetupCTAProps) => {
  const [showBirthdayPicker, setShowBirthdayPicker] = useState(false);
  const [birthdate, setBirthdate] = useState<Date | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation(['timeline', 'common']);

  const handleSetupTimeline = async () => {
    if (!birthdate) {
      toast({
        title: t('setup.toasts.birthdayRequired', { ns: 'timeline' }),
        description: t('setup.toasts.birthdayRequiredDesc', { ns: 'timeline' }),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('initialize_timeline_sections', {
        target_user_id: userId,
        user_birthdate: formatDateForDatabase(birthdate)
      });

      if (error) throw error;

      const result = data as { success?: boolean; sections_created?: number; error?: string };
      if (result?.success) {
        toast({
          title: t('setup.toasts.timelineCreated', { ns: 'timeline' }),
          description: t('setup.toasts.timelineCreatedDesc', { ns: 'timeline', count: result.sections_created || 0 }),
        });
        onTimelineSetup?.();
      } else {
        throw new Error(result?.error || 'Failed to create timeline');
      }
    } catch (error) {
      console.error('Failed to setup timeline:', error);
      toast({
        title: t('setup.toasts.setupFailed', { ns: 'timeline' }),
        description: error instanceof Error ? error.message : t('setup.toasts.setupFailedDesc', { ns: 'timeline' }),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showBirthdayPicker) {
    return (
      <Card className="uplivion-card">
        <CardContent className="space-y-4 pt-6">
          <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold">{t('setup.birthdayLabel', { ns: 'timeline' })}</h3>
            <p className="text-sm text-muted-foreground">{t('setup.pickerDescription', { ns: 'timeline' })}</p>
          </div>
          
          <DateWheelPicker
            value={birthdate}
            onChange={setBirthdate}
            defaultDate={new Date(1995, 0, 26)}
          />
          
          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleSetupTimeline}
              disabled={!birthdate || isLoading}
              className="w-full bg-uplivion-blue hover:bg-blue-700"
            >
              {isLoading ? t('setup.creatingButton', { ns: 'timeline' }) : t('setup.createButton', { ns: 'timeline' })}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowBirthdayPicker(false)}
              disabled={isLoading}
              className="w-full"
            >
              {t('setup.cancelButton', { ns: 'timeline' })}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="uplivion-card">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2 text-xl">
          <Plus className="h-8 w-8 text-uplivion-blue" />
          {t('setup.cardTitle', { ns: 'timeline' })}
        </CardTitle>
        <CardDescription className="text-base">
          {t('setup.cardDescription', { ns: 'timeline' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('setup.detailsText', { ns: 'timeline' })}
          </p>
          <Button 
            onClick={() => setShowBirthdayPicker(true)}
            size="lg"
            className="bg-uplivion-blue hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('setup.getStartedButton', { ns: 'timeline' })}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};